import React from 'react';
import {
    Box,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    Tooltip,
    Typography
} from '@mui/material';
import {
    MyLocation as TargetMuscleIcon,
    Lock as LockIcon,
    Timer as TimerIcon
} from '@mui/icons-material';
import { RTL } from '../../../cache/CacheRtl';
import { targetMuscles } from '../../../enums/TargetMuscles';
import { resistanceTypes } from '../../../enums/resistanceTypes';

/**
 * ClassificationSection - Target muscle and resistance type dropdowns
 */
const ClassificationSection = ({
    formValues,
    setFieldValue,
    setFieldTouched,
    errors,
    touched,
    exercise,
    isSuperAdmin = false
}) => {
    // Check if classification fields should be locked
    const isVariant = exercise?.variant || exercise?.parentExerciseId;
    const isSystemResource = exercise?.systemResource && !isSuperAdmin;
    const isLocked = isVariant || isSystemResource;

    const getLockTooltip = (field) => {
        if (isVariant) {
            return `לא ניתן לשנות ${field === 'targetMuscle' ? 'שריר ראשי' : 'סוג התנגדות'} בגרסה מותאמת - שדה זה נקבע על ידי התרגיל המקורי`;
        }
        if (isSystemResource) {
            return 'רק מנהל מערכת יכול לשנות שדה זה בתרגיל מערכת';
        }
        return '';
    };

    const selectStyles = {
        backgroundColor: isLocked ? '#F8FAFC' : '#FFFFFF',
        borderRadius: '8px',
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#E2E8F0'
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: isLocked ? '#E2E8F0' : '#CBD5E1'
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#57837E'
        },
        '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: '#EF4444'
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TargetMuscleIcon sx={{ color: '#64748B', fontSize: '20px' }} />
                <Typography
                    sx={{
                        fontWeight: 600,
                        fontSize: '16px',
                        color: '#1E293B',
                        fontFamily: '"Heebo", "Roboto", sans-serif'
                    }}
                >
                    סיווג התרגיל
                </Typography>
                {isLocked && (
                    <Tooltip title={getLockTooltip('targetMuscle')}>
                        <LockIcon sx={{ color: '#94A3B8', fontSize: '16px' }} />
                    </Tooltip>
                )}
            </Box>

            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <RTL>
                        <Tooltip title={isLocked ? getLockTooltip('targetMuscle') : ''} placement="top">
                            <FormControl
                                fullWidth
                                error={touched.targetMuscle && Boolean(errors.targetMuscle)}
                                disabled={isLocked}
                            >
                                <InputLabel>שריר ראשי *</InputLabel>
                                <Select
                                    value={formValues.targetMuscle || ''}
                                    onChange={(e) => setFieldValue('targetMuscle', e.target.value)}
                                    onBlur={() => setFieldTouched('targetMuscle')}
                                    label="שריר ראשי *"
                                    sx={selectStyles}
                                >
                                    <MenuItem value="">
                                        <Typography sx={{ fontSize: '14px', color: '#94A3B8' }}>
                                            בחר שריר
                                        </Typography>
                                    </MenuItem>
                                    {targetMuscles.map((muscle) => (
                                        <MenuItem key={muscle.value} value={muscle.value}>
                                            <Typography sx={{ fontSize: '14px' }}>
                                                {muscle.name}
                                            </Typography>
                                        </MenuItem>
                                    ))}
                                </Select>
                                {touched.targetMuscle && errors.targetMuscle && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, mx: 1.5 }}>
                                        {errors.targetMuscle}
                                    </Typography>
                                )}
                            </FormControl>
                        </Tooltip>
                    </RTL>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <RTL>
                        <Tooltip title={isLocked ? getLockTooltip('resistanceType') : ''} placement="top">
                            <FormControl
                                fullWidth
                                error={touched.resistanceType && Boolean(errors.resistanceType)}
                                disabled={isLocked}
                            >
                                <InputLabel>סוג התנגדות *</InputLabel>
                                <Select
                                    value={formValues.resistanceType || ''}
                                    onChange={(e) => setFieldValue('resistanceType', e.target.value)}
                                    onBlur={() => setFieldTouched('resistanceType')}
                                    label="סוג התנגדות *"
                                    sx={selectStyles}
                                >
                                    <MenuItem value="">
                                        <Typography sx={{ fontSize: '14px', color: '#94A3B8' }}>
                                            בחר סוג התנגדות
                                        </Typography>
                                    </MenuItem>
                                    {resistanceTypes.map((type) => (
                                        <MenuItem key={type.value} value={type.value}>
                                            <Typography sx={{ fontSize: '14px' }}>
                                                {type.name}
                                            </Typography>
                                        </MenuItem>
                                    ))}
                                </Select>
                                {touched.resistanceType && errors.resistanceType && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, mx: 1.5 }}>
                                        {errors.resistanceType}
                                    </Typography>
                                )}
                            </FormControl>
                        </Tooltip>
                    </RTL>
                </Grid>
            </Grid>

            {/* Catalyst Toggle */}
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <RTL>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={formValues.catalyst || false}
                                onChange={(e) => setFieldValue('catalyst', e.target.checked)}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#FF6B00' },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#FF6B00' },
                                }}
                            />
                        }
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TimerIcon sx={{ fontSize: 18, color: formValues.catalyst ? '#FF6B00' : '#94A3B8' }} />
                                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: formValues.catalyst ? '#FF6B00' : '#64748B' }}>
                                    תרגיל מבוסס זמן
                                </Typography>
                            </Box>
                        }
                        labelPlacement="start"
                        sx={{ ml: 0 }}
                    />
                </RTL>
            </Box>
        </Box>
    );
};

export default ClassificationSection;
